import { useEffect } from 'react'

import {
  type Control,
  Controller,
  type FieldValues,
  type UseFormSetValue,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  FieldRoot,
  HStack,
  IconButton,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaLockRegular,
  FaPlusRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import type { BusinessMasterRow } from '~/hooks/businessMaster/useListBusinessMasters'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import { resolveAsOfDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  // Control<FieldValues> は任意の form schema を受けつつ RHF の推論を保つ最も広い型。
  // BG マスタ / 発電所マスタの 2 つの schema で共有するため schema 固有型をここで消す
  // (= 既存 _common セクションと同じく呼び出し側で `as unknown as Control<FieldValues>`)。
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  setValue: UseFormSetValue<FieldValues>
  /** i18n ページキー namespace */
  i18nNs: 'adminOcctoMaster.bgMaster' | 'adminOcctoMaster.plantMaster'
  /**
   * 事業者マスタ候補一覧。 各行の select には、 この中から行の対象日時点で
   * 有効なものだけを出す (AC-MOI-023)。
   */
  businessMasters: BusinessMasterRow[]
  /** レコードの適用開始日。 行の適用開始日が無い間の対象日になる (AC-MOI-023)。 */
  recordValidFrom: CivilDate | null | undefined
  /**
   * 代表事業者フィールドのパス
   * (BG マスタ: 'repBusinessMasterId' / 発電所マスタ: 'businessMasterId')。
   * このフィールドの値を members[0] に同期する (= 代表はフラグでなく
   * businessMasterId の一致で表現する)。
   */
  repFieldName: 'repBusinessMasterId' | 'businessMasterId'
  /**
   * true の場合、 各行に ValidFrom / ValidTo の日付入力欄を表示する
   * (BG マスタの構成事業者のみ、 spec bg.md L25 / AC-02 / AC-03)。
   * 発電所マスタの合同所有者は plant 自体の validFrom を全 member に適用する
   * 既存パターン (CreatePlantMasterModal 参照) のため、 省略時は false
   * (= 発電所マスタ側の画面・schema は変更しない)。
   */
  showValidityPeriod?: boolean
  /**
   * true の場合、 見出し ⓘ tooltip (合同 BG の自社集計挙動の説明) と各行の
   * 事業者列見出しラベルを表示する (BG マスタの構成事業者のみ、 design HTML
   * に発電所マスタ「合同所有者」の同等セクションは存在しないため)。
   * 省略時は false (= 発電所マスタ側は装飾なし)。
   */
  showBgDecorations?: boolean
  /**
   * 選択中の value が行の候補に無い時の表示ラベルを返す。 asOf は行の対象日。
   */
  businessMasterFallbackLabel?: (
    id: string,
    asOf: CivilDate,
  ) => string | undefined
}

/**
 * 代表 + 自社メンバーを編集する共有セクション (BG マスタ「構成事業者」/
 * 発電所マスタ「合同所有者」)。
 *
 * 各行 = 事業者 select + 自社 (isOwn) checkbox + 行追加/削除。
 * members[0] は代表事業者と同期する行 (= 代表事業者セレクタ駆動): 事業者 select は
 * readonly・行削除も不可とし、 自社 checkbox のみ編集可能。 代表はフラグでなく
 * businessMasterId の一致で表現する (= bg_master.rep_business_master_id ∈ members)。
 *
 * 代表同期は useFieldArray の update (= 行 remount) ではなく setValue で in-place に
 * 行う。 update は行を unmount/remount するため、 同期前に取得した自社 checkbox の
 * DOM 参照が stale 化する (= F2 test が代表選択後に同参照の checked を assert する)。
 */
export const OwnerMembersSection = ({
  control,
  setValue,
  i18nNs,
  businessMasters,
  recordValidFrom,
  repFieldName,
  showValidityPeriod = false,
  showBgDecorations = false,
  businessMasterFallbackLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name: 'members' })
  const watchedMembers = useWatch({ control, name: 'members' }) as
    | Array<{ validFrom?: CivilDate | null }>
    | undefined
  const today = useTodayJst()
  const { errors } = useFormState({ control, name: 'members' })
  const membersError = errors.members as
    | { message?: string; root?: { message?: string } }
    | undefined

  // 代表事業者の現在値 / members[0] の現在値 (= 同期判定に使う)。 useFieldArray の
  // fields は re-render で stale になるため useWatch で最新値を読む。
  const repBusinessMasterId =
    (useWatch({ control, name: repFieldName }) as string | undefined) ?? ''
  const firstMemberBusinessMasterId =
    (useWatch({ control, name: 'members.0.businessMasterId' }) as
      | string
      | undefined) ?? ''

  // 代表事業者を合同所有者の先頭 (members[0]) に同期する
  // (mock-app RepresentativeMemberSync 準拠)。 代表が変わったら members[0] を差し替え、
  // 先頭が未入力なら自社 (isOwn=true) として埋める。 PlanMasterBgsSection と同じく
  // rep 変化のみを契機にするため dep は [repBusinessMasterId] に限定する。
  useEffect(() => {
    if (firstMemberBusinessMasterId === repBusinessMasterId) {
      return
    }
    const wasEmpty = firstMemberBusinessMasterId === ''
    setValue('members.0.businessMasterId', repBusinessMasterId, {
      shouldDirty: true,
    })
    if (repBusinessMasterId !== '' && wasEmpty) {
      setValue('members.0.isOwn', true, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repBusinessMasterId])

  const addMember = (): void => {
    append(
      showValidityPeriod
        ? {
            businessMasterId: '',
            isOwn: false,
            validFrom: todayCivilDate(),
            validTo: undefined,
          }
        : { businessMasterId: '', isOwn: false },
    )
  }

  return (
    <Stack gap={1}>
      {/* Section header (design `.occto-sub-header`):
          - display:flex + justify-content:space-between + align-items:center
          - margin-top: space-4 / padding-top: space-3 / border-top: 1px Border.lowEmphasis
          - 左 = 見出し (font-size lg = 16px / weight 700 / high-emphasis) + ⓘ tooltip
          - 右 = 「追加」button (TertiaryButton size sm + FaPlusRegular)
          追加ボタンは design ではセクションヘッダの右端にあり、 従来 impl の
          「行リストの下」から header に移動する。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <HStack align="center" gap={1}>
          {/* group label は FieldRoot 配下ではなく素の Text にする (FieldRoot 配下に
              置くと ecloud v3 Field context が Checkbox の accessible name を
              群ラベルへ差し替えて自社 checkbox が押せなくなる)。 label 属性は
              members error の <FieldErrorText> 集約側の FieldRoot に付与する。 */}
          <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
            {t(`page:${i18nNs}.form.members`)}
          </Text>
          {/* 自社チェックの補足 ? は**見出しではなく各行の「自社」 の横**に置く
              (2026-08-26 デザイナー判断)。 文言が丸ごと 自社 checkbox の説明なので、
              コントロールの真横で読めた方が探しやすい。 下の row 内を参照。 */}
        </HStack>
        <TertiaryButton onClick={addMember} size="sm">
          <FaPlusRegular />
          {t(`page:${i18nNs}.form.memberAdd`)}
        </TertiaryButton>
      </HStack>
      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="md" paddingY={2}>
            {t(`page:${i18nNs}.form.memberEmpty`)}
          </Text>
        )}
        {fields.map((field, index) => {
          // members[0] は代表事業者と同期する行 (= 代表事業者セレクタ駆動)。
          const isRepresentative = index === 0
          // 発電所の合同所有者は行の適用期間を持たず、 発電所の適用開始日が全行の対象日になる。
          const asOf = resolveAsOfDate(
            showValidityPeriod ? watchedMembers?.[index]?.validFrom : undefined,
            recordValidFrom,
            today,
          )
          return (
            <Box
              key={field.id}
              position="relative"
              // design `.occto-member-row.is-rep` は代表行を info-low で塗り分ける。
              // 通常行は Surface.secondary (グレー) にして「セクション内の 1 単位」
              // を視覚的に明示する (= 単なる HStack 並列よりまとまりが読める)。
              // `position="relative"`: 行の操作 (代表 lock / 削除) をカード右上に
              // 絶対配置するため (下記コメント参照)。
              // padding 16px (2026-08-26 デザイナー指摘)。 8px だと 2 段組の中身に
              // 対してカードの余白が足りず、 塗り分けが「枠」 でなく「下敷き」 に見えた。
              padding={4}
              borderRadius="base"
              backgroundColor={
                isRepresentative ? 'Surface.infoLow' : 'Surface.secondary'
              }
            >
              {/* 行の操作はカードの**右上**に置く (2026-08-26 デザイナー指摘)。
                  以前は 1 段目の末尾 (事業者 select と同じ行) に並べていたため、
                  2 段組 (事業者 + 有効期間) のカードの中で「左隣の事業者項目だけに
                  掛かる操作」 に見えていた。 lock も削除もカード = 構成事業者 1 件
                  全体に対する操作なので、 カード全体を指す位置へ出す。 */}
              <Box position="absolute" zIndex={1} top={4} right={4}>
                {isRepresentative ? (
                  <ButtonTooltip
                    content={t(`page:${i18nNs}.form.memberRepresentativeHint`)}
                  >
                    <Box
                      alignItems="center"
                      justifyContent="center"
                      display="inline-flex"
                      width="32px"
                      height="32px"
                      color="Text.midEmphasis"
                      aria-label={t(`page:${i18nNs}.form.memberRepresentative`)}
                      title={t(`page:${i18nNs}.form.memberRepresentativeHint`)}
                    >
                      <FaLockRegular />
                    </Box>
                  </ButtonTooltip>
                ) : (
                  <IconButton
                    color="Text.negativeMid"
                    _hover={{ color: 'Text.negativeHigh' }}
                    aria-label={t(`page:${i18nNs}.form.memberRemove`)}
                    onClick={() => {
                      remove(index)
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <FaXmarkRegular />
                  </IconButton>
                )}
              </Box>
              {/* 1 段目 = 事業者 select + 自社 + 代表 lock / 削除、
                  2 段目 = 有効期間 (ValidFrom / ValidTo)。
                  showValidityPeriod=true (BG マスタ) の時のみ 2 段目を出す。
                  1 段に横並びだと anchor 幅が狭くて design の carousel/date picker
                  と大きさが揃わなかったため、 2 段組で行の高さを取り幅を確保する
                  (2026-08-05 ユーザー指示)。 */}
              <Stack gap={2}>
                {/* `paddingRight`: 右上へ絶対配置した操作 (32px) の下に
                    自社 checkbox が潜り込まないようぶんだけ空ける。 */}
                <HStack align="flex-end" gap={2} paddingRight="32px">
                  {/* 事業者 select (代表行は readonly)。
                      design (`design/admin/occto.html` #bgm-members の
                      `_bgRenderMembers()`) は各行の上に「事業者コード (代表)」/
                      「事業者名称」の見出しラベルを持つ。 実装は業務モデル上、
                      事業者コード + 名称を FK select 1 個 (businessMasterId) に
                      統合しているため、 design の 2 列見出しをそのまま転記せず
                      1 列見出し「事業者」として可視ラベル化する
                      (= aria-label のみだった状態から見出しを追加、 BG マスタ固有)。 */}
                  <Box flex={1}>
                    {showBgDecorations && (
                      <Text
                        textStyle="label"
                        marginBottom={1}
                        color="Text.highEmphasis"
                      >
                        {/* 代表行は列見出しに「(代表)」 を添える (2026-08-26
                            デザイナー指摘)。 以前は行の塗り (Surface.infoLow) だけが
                            手掛かりで、 **色でしか代表を示していなかった**。
                            8080 `admin/occto.html` の `_bgRenderMembers()` も
                            `rep ? '事業者コード (代表)' : '事業者コード'` と見出しで
                            示しており、 実装は FK select 1 個に統合した 1 列見出しへ
                            読み替える。 DS `Badge` は使わない —
                            「代表」 は状態でなく役割。 */}
                        {t(
                          isRepresentative
                            ? 'page:adminOcctoMaster.bgMaster.form.memberColumnLabelRepresentative'
                            : 'page:adminOcctoMaster.bgMaster.form.memberColumnLabel',
                        )}
                      </Text>
                    )}
                    <FormSelect
                      aria-label={t(`page:${i18nNs}.form.memberBusinessMaster`)}
                      control={control}
                      fallbackLabel={(id) =>
                        businessMasterFallbackLabel?.(id, asOf)
                      }
                      isDisabled={isRepresentative}
                      name={`members.${index}.businessMasterId`}
                      options={businessMasters
                        .filter((bm) =>
                          isActiveOn(bm.validFrom, bm.validTo, asOf),
                        )
                        .map((bm) => ({
                          value: bm.id,
                          label: bm.businessName,
                        }))}
                      placeholder={t(
                        `page:${i18nNs}.form.memberBusinessMasterUnselected`,
                      )}
                    />
                  </Box>
                  {/* 自社 (is_own) checkbox */}
                  {/* 自社 (isOwn) checkbox。 ラベルは **DS の label slot に素の文字列**
                      で渡す (2026-08-26 デザイナー指摘)。 以前は `<Text fontSize="sm">`
                      で包んで 12px に落としていたが、 これは DS recipe の打ち消しで、
                      同じ行の事業者ラベル (14px) と字送りが揃わなかった。 repo 内の他の
                      Checkbox (BusinessAreasCheckboxes / IndeterminateCheckboxGroup 等)
                      も素の文字列を渡す形が多数派。
                      縦位置は `paddingBottom={2}` のマジックナンバーをやめ、 同じ行の
                      代表 lock と同じ 40px の箱で中央寄せにする (行は align="flex-end"
                      で、 隣の select が 40px。 select の size を変えても崩れない)。 */}
                  <Box alignItems="center" gap={1} display="flex" height="40px">
                    <Controller
                      control={control}
                      name={`members.${index}.isOwn`}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          checked={value as boolean}
                          onCheckedChange={(details: {
                            checked: boolean | 'indeterminate'
                          }) => {
                            onChange(details.checked === true)
                          }}
                        >
                          {t(`page:${i18nNs}.form.memberIsOwn`)}
                        </Checkbox>
                      )}
                    />
                    {/* 合同 BG の自社集計挙動の説明。 説明対象である「自社」 の真横に
                        置く (2026-08-26 デザイナー判断)。 */}
                    {showBgDecorations && (
                      <HelpIcon
                        ariaLabel={t(
                          'page:adminOcctoMaster.bgMaster.form.membersTooltipAriaLabel',
                        )}
                        content={t(
                          'page:adminOcctoMaster.bgMaster.form.membersTooltip',
                        )}
                      />
                    )}
                  </Box>
                </HStack>
                {/*
                  有効期間 (ValidFrom / ValidTo)。 BG マスタのみ表示 (AC-02 /
                  AC-03)。 代表行 (index 0) は AC-OMB-021 (issue #6038) により
                  BG 本体の適用期間へ自動同期される readonly 欄 (同期の実処理は
                  BgMasterFormFields 側が BG 本体 validFrom/validTo の変化を
                  watch して行う — 本 component は PlantMaster と共有するため
                  BG 固有の同期ロジックは持たない)。 非代表行は引き続き
                  独立して編集可能。 spacerWidth=0 で末尾 flex balance を消し、
                  2 段目全幅を ValidFrom/ValidTo で使う。
                */}
                {showValidityPeriod && (
                  <PeriodFields
                    basePath={`members.${index}`}
                    clearAsNull
                    control={control}
                    disabled={isRepresentative}
                    i18nNs={i18nNs}
                    spacerWidth="0px"
                  />
                )}
              </Stack>
            </Box>
          )
        })}
      </VStack>
      {membersError !== undefined && (
        <FieldRoot invalid>
          <FieldErrorText>
            {membersError.message ?? membersError.root?.message}
          </FieldErrorText>
        </FieldRoot>
      )}
    </Stack>
  )
}
